import { nextTick, reactive } from "vue";
import { defineStore } from "pinia";
import { getBaseEndpoint, baseApiEndpoint } from "@/config/config";
import { FilterOptionsResponseModel } from "@/services/campayacore-api.service";

let BASE_API = getBaseEndpoint(process.env.NODE_ENV);

// so that the price calculator works
if (window.location.host === "localhost:7030") {
  BASE_API = baseApiEndpoint.staging;
}

export interface Facilities {
  name: string;
  value: boolean;
}

export interface FilterObject {
  adults?: number | undefined;
  areaId?: number | undefined;
  areaName?: string | undefined;
  bathrooms?: number | undefined;
  bedrooms?: (number|undefined)[] | undefined;
  beds?: number[] | undefined;
  bedsFrom?: number | undefined;
  bedsTo?: number | undefined;
  children?: number | undefined;
  infants?: number | undefined;
  dateRange?: Date[] | undefined;
  distanceToWater?: any | undefined;
  facilities?: any | undefined;
  flexibleDates?: boolean | undefined;
  fromBedrooms?: number | undefined;
  fromDate?: Date | undefined;
  fromPrice?: number | undefined;
  hasActiveFilters: boolean;
  houseType?: any | undefined;
  kidsFacilities?: string[] | undefined;
  otherFacilities?: string[] | undefined;
  outdoorFacilities?: string[] | undefined;
  payLater?: boolean | undefined;
  petsAllowed?: boolean | undefined;
  pets?: number | undefined;
  priceRange?: (number|undefined)[];
  propertyType?: number | undefined;
  rooms?: string | undefined;
  smokingAllowed?: boolean | undefined;
  sortMethod?: number | undefined;
  themeId?: number | undefined;
  toBedrooms?: number | undefined;
  toDate?: Date | undefined;
  toPrice?: number | undefined;
  mapLoadCounter?: number;
  mapZoom?: number;
  mapCenter?: any;
  hasLastMinuteSearchAvailability?: boolean | undefined;
  boostHasLastMinuteSearchAvailability?: boolean | undefined;
  privateOwner?: boolean;
  flexibleCancellation?: boolean;
  filterOptions?: FilterOptionsResponseModel,
  isUserZoom?: boolean;
}

export const defaultFilterObject: FilterObject = {
  mapLoadCounter: 0,
  mapCenter: { lng: 0, lat: 40 },
  mapZoom: 1,
  isUserZoom: false,
  hasActiveFilters: false,
  areaId: 25,
  adults: undefined,
  areaName: undefined,
  bathrooms: undefined,
  beds: [0, 12],
  bedsFrom: undefined,
  bedsTo: undefined,
  bedrooms: [1, undefined],
  fromBedrooms: undefined,
  toBedrooms: undefined,
  children: undefined,
  infants: undefined,
  dateRange: [new Date(), new Date()],
  toDate: undefined,
  fromDate: undefined,
  distanceToWater: { text: "Alle", value: undefined },
  facilities: {
    ac: undefined,
    barbecue: undefined,
    coffeeMachine: undefined,
    dishwasher: undefined,
    fireplace: undefined,
    hotTub: undefined,
    internet: undefined,
    outdoorSpa: undefined,
    indoorPool: undefined,
    sauna: undefined,
    spa: undefined,
    tv: undefined,
    washingMachine: undefined,
    wheelchairFriendly: undefined,
    evChargingStation: undefined,
    outdoorPool: undefined,
    germanChannels: undefined,
    dryer: undefined,
    scandinavianChannels: undefined,
    chromeCast: undefined
  },
  houseType: {
    value: undefined,
  },
  kidsFacilities: undefined,
  outdoorFacilities: undefined,
  otherFacilities: undefined,
  payLater: undefined,
  priceRange: [0, undefined],
  fromPrice: undefined,
  toPrice: undefined,
  propertyType: undefined,
  themeId: undefined,
  sortMethod: 1,
  smokingAllowed: undefined,
  petsAllowed: undefined,
  pets: undefined,
  hasLastMinuteSearchAvailability: undefined,
  boostHasLastMinuteSearchAvailability: undefined,
  privateOwner: undefined,
  flexibleCancellation: undefined,
  filterOptions: undefined
};

export const useFilterStore= defineStore(
  "filter",
  () => {
    // state properties
    const filterObject: FilterObject = reactive(
      structuredClone(defaultFilterObject)
    );

    // actions
    /**
     * Method for updating the filters based on the url
     */
    const updateFiltersFromUrl = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paramsObj: any = {};

      urlParams.forEach((value, key) => {
        paramsObj[key] = value;
      });

      if (paramsObj.areaId) filterObject.areaId = Number(paramsObj.areaId);
      if (paramsObj.areaName) filterObject.areaName = paramsObj.areaName;

      filterObject.adults = paramsObj.adults ? Number(paramsObj.adults) : 0;
      filterObject.children = paramsObj.children ? Number(paramsObj.children) : 0;
      filterObject.pets = paramsObj.pets ? Number(paramsObj.pets) : 0;

      if (paramsObj.petsAllowed === 'true') filterObject.petsAllowed = true;
      if (paramsObj.petsAllowed === 'false') filterObject.petsAllowed = false;
      if (paramsObj.pets && Number(paramsObj.pets) > 0) filterObject.petsAllowed = true;
      if (!paramsObj.petsAllowed && !paramsObj.pets) filterObject.petsAllowed = undefined;

      if (paramsObj.themeId) setFiltersByThemeId(Number(paramsObj.themeId));

      filterObject.fromDate = paramsObj.fromDate ? new Date(paramsObj.fromDate) : undefined;
      filterObject.toDate = paramsObj.toDate ? new Date(paramsObj.toDate) : undefined;
    };

    /**
     * Method for clearing the filters
     */
    const clearFilters = () => {
      filterObject.hasActiveFilters = false;
      filterObject.priceRange = [0, 24500];
      filterObject.bedrooms = [0, 20];
      filterObject.rooms = "Alle";
      filterObject.distanceToWater.text = "Alle";
      filterObject.distanceToWater.value = undefined;
      filterObject.facilities = structuredClone(defaultFilterObject.facilities);
      filterObject.smokingAllowed = undefined;
      filterObject.petsAllowed = undefined;
      filterObject.payLater = undefined;
      filterObject.themeId = undefined;
      filterObject.hasLastMinuteSearchAvailability = undefined;
      filterObject.boostHasLastMinuteSearchAvailability = undefined;
      filterObject.privateOwner = undefined;
      filterObject.flexibleCancellation = undefined;

      // remove filters from url
      const urlParams = new URLSearchParams(window.location.search);
      const paramsObj: any = {};

      urlParams.forEach((value, key) => {
        paramsObj[key] = value;
      });

      const searchParams = new URLSearchParams()
      if (paramsObj.areaId) searchParams.append("areaId", paramsObj.areaId)
      if (paramsObj.areaName) searchParams.append("areaName", paramsObj.areaName)
      if (paramsObj.themeId) searchParams.append("themeId", paramsObj.themeId)
      if (paramsObj.adults) searchParams.append("adults", paramsObj.adults)
      if (paramsObj.children) searchParams.append("children", paramsObj.children)
      if (paramsObj.infants) searchParams.append("infants", paramsObj.infants)
      if (paramsObj.pets) searchParams.append("pets", paramsObj.pets)
      if (paramsObj.fromDate) searchParams.append("fromDate", paramsObj.fromDate)
      if (paramsObj.toDate) searchParams.append("toDate", paramsObj.toDate)

      window.history.replaceState({}, "", "/search?" + searchParams.toString())
    };

    const setFiltersByThemeId = (themeId: number | undefined) => {
      let shouldDeleteThemeFromParams = true

      filterObject.hasActiveFilters = true

      // TODO: Uncomment setPropertyTrue when implemented
      switch (themeId) {
        case 336:
          filterObject.facilities.indoorPool = true;
          filterObject.facilities.outdoorPool = true;
          updateStringUrlSearchParams("indoorPool");
          updateStringUrlSearchParams("outdoorPool");
          break;
        case 337:
        case 342:
          filterObject.facilities.indoorPool = true;
          filterObject.facilities.outdoorPool = true;
          // setPropertyTrue(212); // sommerhus
          updateStringUrlSearchParams("indoorPool");
          updateStringUrlSearchParams("outdoorPool");
          updateStringUrlSearchParams("propertyType", false);
          break;
        case 338:
        case 340:
        case 343:
          filterObject.petsAllowed = true;
          updateStringUrlSearchParams("petsAllowed", false);
          break;
        case 334:
        case 335:
          filterObject.facilities.wheelchairFriendly = true;
          updateStringUrlSearchParams("wheelchairFriendly");
          break;
        case 339:
        case 344: // Billig ferie
          filterObject.sortMethod = 2;
          updateStringUrlSearchParams("sortMethod", false);
          break;
        case 341: // Billig sommmerhuse
          filterObject.sortMethod = 2;
          //setPropertyTrue(212); // sommerhus
          updateStringUrlSearchParams("propertyType", false);
          updateStringUrlSearchParams("sortMethod", false);
          break;
        case 114: // Luksussommerhuse
        case 166: // Luxus
        case 49: // Luxury
          filterObject.sortMethod = 3;
          updateStringUrlSearchParams("sortMethod", false);
          break;
        case 121: // Skiferie i skihytte
        case 173: // Skigebiete
        case 248: // Skidstugor
        case 82: // Skiing
          filterObject.themeId = 82;
          filterObject.hasActiveFilters = hasActiveFilters();
          shouldDeleteThemeFromParams = false;
          break;
        default:
          filterObject.themeId = themeId;
          filterObject.hasActiveFilters = hasActiveFilters();
          shouldDeleteThemeFromParams = false;
          break;
      }

      if (shouldDeleteThemeFromParams) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete("themeId");
        filterObject.themeId = undefined;
        window.history.replaceState({}, "", "/search?" + urlParams.toString());
      }
    };

    const updateStringUrlSearchParams = (paramEntry: string, isFacility = true) => {
      const urlSearchParams = new URLSearchParams(window.location.search);

      const existingValue = urlSearchParams.get(paramEntry);

      if (!isFacility) {
        if (filterObject[paramEntry as keyof FilterObject] == defaultFilterObject[paramEntry as keyof FilterObject]) {
          urlSearchParams.delete(paramEntry);
        } else {
          urlSearchParams.set(paramEntry, filterObject[paramEntry as keyof FilterObject]);
        }
      }

      if (
        existingValue !== null &&
        filterObject.facilities[paramEntry] === false &&
        isFacility
      ) {
        urlSearchParams.delete(paramEntry);
      } else if (isFacility) {
        urlSearchParams.set(paramEntry, filterObject.facilities[paramEntry]);
      }

      const newUrl = `${window.location.origin}${
        window.location.pathname
      }?${urlSearchParams.toString()}`;

      window.history.pushState({ path: newUrl }, "", newUrl);
    };



    const setPropertyTrue = (propertyTypeId: number) => {
      filterObject.houseType.value = propertyTypeId;
      filterObject.propertyType = propertyTypeId;
    };



    const toggleFacility = (filter: string, onValue: any) => {
      const urlSearchParams = new URLSearchParams(window.location.search);

      if (filterObject.facilities[filter] == onValue) {
        filterObject.facilities[filter] = undefined;
        urlSearchParams.delete(filter);
      } else {
        filterObject.facilities[filter] = onValue;
        urlSearchParams.set(filter, onValue);
      }

      return urlSearchParams;
    };



    const toggleFilter = (filter: string, onValue: any) => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      if (filterObject[filter] == onValue) {
        filterObject[filter] = undefined;
        urlSearchParams.delete(filter);
      } else {
        filterObject[filter] = onValue;
        urlSearchParams.set(filter, onValue);
      }

      return urlSearchParams;
    };

    const toggleFilterOrFacility = (filter: string, onValue: any) => {
      if (filter == 'pool') {

        if (!filterObject.facilities.indoorPool && !filterObject.facilities.outdoorPool) {
          toggleFilterOrFacility('indoorPool', onValue);
          toggleFilterOrFacility('outdoorPool', onValue);
          return
        }

        if (filterObject.facilities.indoorPool) {
          toggleFilterOrFacility('indoorPool', onValue);
        }
        if (filterObject.facilities.outdoorPool) {
          toggleFilterOrFacility('outdoorPool', onValue);
        }
        return
      }

      let urlSearchParams;
      if (filter in filterObject.facilities) {
        urlSearchParams = toggleFacility(filter, onValue);
      } else {
        urlSearchParams = toggleFilter(filter, onValue);
      }

      const newUrl = `${window.location.origin}${
        window.location.pathname
      }?${urlSearchParams.toString()}`;

      window.history.pushState({ path: newUrl }, "", newUrl);
    };

    const setFacilityFilter = (filter: string, value: boolean) => {
      const urlSearchParams = new URLSearchParams(window.location.search);

      if (value == false) {
        filterObject.facilities[filter] = undefined;
        urlSearchParams.delete(filter);
      } else {
        filterObject.facilities[filter] = value;
        urlSearchParams.set(filter, 'true');
      }

      const newUrl = `${window.location.origin}${
        window.location.pathname
      }?${urlSearchParams.toString()}`;

      window.history.pushState({ path: newUrl }, "", newUrl);
    }

    const hasActiveFilters = () => {
      filterObject.hasActiveFilters =
        filterObject.bathrooms ||
        filterObject.fromBedrooms != defaultFilterObject.fromBedrooms ||
        filterObject.toBedrooms != defaultFilterObject.toBedrooms ||
        filterObject.bedrooms ||
        filterObject.distanceToWater.value ||
        filterObject.petsAllowed ||
        filterObject.smokingAllowed ||
        filterObject.payLater ||
        filterObject.kidsFacilities ||
        filterObject.outdoorFacilities ||
        filterObject.otherFacilities ||
        filterObject.propertyType ||
        filterObject.fromPrice!= defaultFilterObject.fromPrice ||
        filterObject.toPrice != defaultFilterObject.toPrice ||
        filterObject.privateOwner ||
        filterObject.flexibleCancellation ||
        Object.values(filterObject.facilities).some(x => x);

      return filterObject.hasActiveFilters;
    };

    /**
   * Updates the search params and store based on the key and value and sends an emit to parent component that handles the search execution
   * @param key {string} - The key of the search param
   * @param value The value to update with - string, numbers and/or objects and arrays
   */
  const updateUrlParamAndDoSearch = (key: keyof FilterObject | 'lastMinute', value: any) => {
    const baseUrl = window.location.href.split('?')[0];
    const params = new URLSearchParams(window.location.search)


    // if anything but priceRange and beds
    if (key !== 'distanceToWater' && key !== 'priceRange' && key !== 'bedrooms' && key !== 'dateRange' && key !== 'lastMinute') {
      if (value === undefined) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
      filterObject[key] = value

      window.history.replaceState({}, '', `${baseUrl}?${params.toString()}`)
      hasActiveFilters();
      return true;
    }


    if (key === 'priceRange') {
      if (value[0] == undefined || value[0] == 0) {
        params.delete('fromPrice')
      } else {
        params.set('fromPrice', value[0])
      }
      if (value[1] == undefined || value[1] == 20000) {
        params.delete('toPrice')
      } else {
        params.set('toPrice', value[1])
      }
      filterObject.priceRange = value
      filterObject.fromPrice = value[0]
      filterObject.toPrice = value[1]

      window.history.replaceState({}, '', `${baseUrl}?${params.toString()}`)
      hasActiveFilters();
      return true;
    }

    if (key === 'dateRange') {
      if (value[0] == undefined) {
        params.delete('fromDate')
      } else {
        params.set('fromDate', value[0])
      }
      if (value[1] == undefined) {
        params.delete('toDate')
      } else {
        params.set('toDate', value[1])
      }
      filterObject.dateRange = value
      filterObject.fromDate = value[0]
      filterObject.toDate = value[1]

      window.history.replaceState({}, '', `${baseUrl}?${params.toString()}`)
      hasActiveFilters();
      return true;
    }

    if (key === 'bedrooms') {
      if (value[0] == undefined) {
        params.delete('fromBedrooms')
      } else {
        params.set('fromBedrooms', value[0])
      }
      if (value[1] == undefined) {
        params.delete('toBedrooms')
      } else {
        params.set('toBedrooms', value[1])
      }
      filterObject.bedrooms = value
      filterObject.fromBedrooms = value[0]
      filterObject.toBedrooms = value[1]

      window.history.replaceState({}, '', `${baseUrl}?${params.toString()}`)
      hasActiveFilters();
      return true;
    }


    if (key === 'distanceToWater') {
      if (value == undefined || (typeof value === 'object' && value.value == undefined)) {
        params.delete("distanceToWater")
      } else {
        params.set('distanceToWater', value)
      }
      if (typeof value === 'object') {
        filterObject.distanceToWater = value
      } else {
        filterObject.distanceToWater.value = value // value = { text: '1000m', value: 1000 }
      }

      window.history.replaceState({}, '', `${baseUrl}?${params.toString()}`)

      //updateFiltersInStore()
      hasActiveFilters();
      return true;
    }

    if (key === 'lastMinute') {
      if (value === undefined) {
        params.delete(key);
      } else {
        params.set('lastMinute', value)
      }
      filterObject.hasLastMinuteSearchAvailability = value
      window.history.replaceState({}, '', `${baseUrl}?${params.toString()}`)
      hasActiveFilters();
      return true;
    }

    return false;
  }

    return {
      filterObject,
      clearFilters,
      setFiltersByThemeId,
      updateStringUrlSearchParams,
      setPropertyTrue,
      hasActiveFilters,
      updateFiltersFromUrl,
      toggleFilterOrFacility,
      updateUrlParamAndDoSearch,
      setFacilityFilter
    };
  },
  { persist: false }
);
